import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { RiBarChart2Line } from 'react-icons/ri'

import {
  SENSEMAKER_OVERVIEW_BURN_CHART_AXIS_TICK_COLOR,
  SENSEMAKER_OVERVIEW_BURN_CHART_EMPTY_ICON_BG,
  SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR,
  SENSEMAKER_OVERVIEW_BURN_CHART_TITLE_TO_GRAPH_GAP_PX,
} from '../../lib/sensemaker-overview-layout'

const EMPTY_WEEKS = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8'] as const

const EMPTY_Y_TICKS = ['100%', '80%', '60%', '40%', '20%', '0%'] as const

const Y_TITLE_WIDTH_PX = 22
const Y_TICK_WIDTH_PX = 38
const LEFT_GUTTER_WIDTH_PX = Y_TITLE_WIDTH_PX + Y_TICK_WIDTH_PX
const X_TICK_ROW_HEIGHT_PX = 18
const X_TITLE_HEIGHT_PX = 16
const X_AXIS_GAP_PX = 4

const axisTickSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: 'normal',
  color: SENSEMAKER_OVERVIEW_BURN_CHART_AXIS_TICK_COLOR,
  fontFamily: 'Inter, sans-serif',
} as const

const axisTitleSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: 'normal',
  color: '#000',
  fontFamily: 'Inter, sans-serif',
} as const

/** Figma `17:898`–`17:942` — grid + centered empty state when no burn data. */
export function SensemakerTokenBurnChartEmptyState() {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        mt: `${SENSEMAKER_OVERVIEW_BURN_CHART_TITLE_TO_GRAPH_GAP_PX}px`,
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid="sensemaker-token-burn-chart-empty"
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <Box
          sx={{
            width: `${Y_TITLE_WIDTH_PX}px`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              ...axisTitleSx,
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              whiteSpace: 'nowrap',
            }}
          >
            Token balance
          </Typography>
        </Box>

        <Box
          sx={{
            width: `${Y_TICK_WIDTH_PX}px`,
            flexShrink: 0,
            position: 'relative',
          }}
        >
          {EMPTY_Y_TICKS.map((tick, index) => (
            <Typography
              key={tick}
              sx={{
                position: 'absolute',
                right: 0,
                top: `calc(${index} * 100% / 5)`,
                transform: 'translateY(-50%)',
                textAlign: 'right',
                ...axisTickSx,
              }}
            >
              {tick}
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
            borderLeft: `1px solid ${SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR}`,
            borderBottom: `1px solid ${SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR}`,
            backgroundImage: `
              repeating-linear-gradient(
                to bottom,
                ${SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR} 0,
                ${SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR} 1px,
                transparent 1px,
                transparent calc(100% / 6)
              ),
              repeating-linear-gradient(
                to right,
                ${SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR} 0,
                ${SENSEMAKER_OVERVIEW_BURN_CHART_GRID_COLOR} 1px,
                transparent 1px,
                transparent calc(100% / 8)
              )
            `,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '9px',
              pointerEvents: 'none',
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '10px',
                bgcolor: SENSEMAKER_OVERVIEW_BURN_CHART_EMPTY_ICON_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <RiBarChart2Line size={24} color="#fff" aria-hidden />
            </Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                color: '#000',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              No data yet
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                color: '#64748b',
                fontFamily: 'Inter, sans-serif',
                textAlign: 'center',
                maxWidth: 280,
              }}
            >
              Token burn trend will display here after the token module is implemented in a
              future story.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexShrink: 0,
          mt: `${X_AXIS_GAP_PX}px`,
        }}
      >
        <Box sx={{ width: `${LEFT_GUTTER_WIDTH_PX}px`, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              height: `${X_TICK_ROW_HEIGHT_PX}px`,
              alignItems: 'center',
            }}
          >
            {EMPTY_WEEKS.map((week) => (
              <Typography
                key={week}
                sx={{
                  textAlign: 'center',
                  ...axisTickSx,
                }}
              >
                {week}
              </Typography>
            ))}
          </Box>
          <Typography
            sx={{
              ...axisTitleSx,
              height: `${X_TITLE_HEIGHT_PX}px`,
              lineHeight: `${X_TITLE_HEIGHT_PX}px`,
              textAlign: 'center',
            }}
          >
            Week
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
